import { Workflow } from '@automata/wfb-api/src/apis/wfbApiSchemas';

import { ZEROED_NODE_POSITION } from '../../../constants';
import {
  TaskDependenciesById,
  TimeConstraintInstancesById,
} from '../../../features/graph/graphSlice';
import { NodePosition, TimeConstraintHandleType } from '../../../types';
import uuid from '../../../uuid';
import { TaskType } from '../types';

const getTaskId = (taskId: string | { base: string }): string =>
  typeof taskId === 'string' ? taskId : taskId.base;

export const buildNullNodePositions = (
  ids: string[]
): Record<string, NodePosition> => {
  return ids.reduce<Record<string, NodePosition>>((acc, id) => {
    acc[id] = ZEROED_NODE_POSITION;
    return acc;
  }, {});
};

export const getHandlePairIdOrderByNode = (
  labwareIds: string[],
  flatTasksById: Record<string, TaskType>
): Record<string, string[]> => {
  const handlePairIdOrderByNode: Record<string, string[]> = {};

  // Generate handle IDs for nodes that don't have them
  labwareIds.forEach((id) => {
    if (!handlePairIdOrderByNode[id]) {
      handlePairIdOrderByNode[id] = [id];
    }
  });

  Object.keys(flatTasksById).forEach((id) => {
    if (!handlePairIdOrderByNode[id]) {
      // Check if this task has multiple labware sources
      let numHandles = 1;

      if (flatTasksById[id]) {
        const task = flatTasksById[id];
        if ('labware_sources' in task && task.labware_sources) {
          numHandles = Math.max(1, Object.keys(task.labware_sources).length);
        }
      }

      // Generate unique handle IDs
      const handles: string[] = [];
      for (let i = 0; i < numHandles; i++) {
        handles.push(uuid());
      }

      handlePairIdOrderByNode[id] = handles;
    }
  });

  return handlePairIdOrderByNode;
};

export const getTaskDependencies = (
  workflow: Workflow
): TaskDependenciesById => {
  const taskDependencies: Record<
    string,
    { fromTaskId: string; toTaskId: string; id: string }
  > = {};

  Object.entries(workflow.tasks).forEach(([taskId, task]) => {
    if (!('dependencies' in task)) return;

    if (task.dependencies && Array.isArray(task.dependencies)) {
      task.dependencies.forEach((dependencyTaskId) => {
        const dependencyId = uuid();
        taskDependencies[dependencyId] = {
          fromTaskId: getTaskId(dependencyTaskId),
          toTaskId: taskId,
          id: dependencyId,
        };
      });
    }
  });
  return taskDependencies;
};

export const getTimeConstraints = (
  workflow: Workflow
): TimeConstraintInstancesById => {
  // Build timeConstraints from workflow time_constraints
  const timeConstraints: TimeConstraintInstancesById = {};

  workflow.time_constraints.forEach((constraint) => {
    const constraintId = uuid();

    // Parse the constraint type to determine source and target types
    let sourceType: TimeConstraintHandleType;
    let targetType: TimeConstraintHandleType;

    switch (constraint.type) {
      case 'start_to_start':
        sourceType = 'start';
        targetType = 'start';
        break;
      case 'start_to_end':
        sourceType = 'start';
        targetType = 'end';
        break;
      case 'end_to_start':
        sourceType = 'end';
        targetType = 'start';
        break;
      case 'end_to_end':
        sourceType = 'end';
        targetType = 'end';
        break;
      default:
        sourceType = 'start';
        targetType = 'end';
    }

    timeConstraints[constraintId] = {
      source: getTaskId(constraint.start_task_id),
      sourceType,
      target: getTaskId(constraint.end_task_id),
      targetType,
      id: constraintId,
      minDuration: constraint.min_duration ?? 1,
      maxDuration: constraint.max_duration ?? 1,
      enableDetailedCoverageCalculation:
        constraint.enable_detailed_coverage_calculation ?? true,
      autoIgnoreIfBroken: constraint.auto_ignore_if_broken ?? false,
    };
  });

  return timeConstraints;
};
